import { Alert, Spinner } from '@design-system';
import { useTranslation } from 'next-i18next';
import React, { FC, useCallback, useEffect, useState } from 'react';

import { nftsGateway } from '../../../../gateways/NftsApi/NftsApi';
import { useReleaseGating } from '../../../../hooks/useReleaseGating';
import { GeneratedNftPrint } from '../../../../types/generated';
import { ShopifyProductToCheckout } from '../../../../types/Purchasing';
import { customPropertyByKey } from '../../../../utils/product';
import { nftTokenToPrintOption } from '../../../../utils/web3/nftUtils';
import { useDelegateCash } from '../../Web3Provider/useDelegateCash';
import { useWeb3 } from '../../Web3Provider/useWeb3';
import { ImagePicker, NftTokenOption } from './ImagePicker';

interface ImagePickerModuleProps {
  isLoading: boolean;
  onAdd: (token: NftTokenOption) => void;
  onRemove: (token: NftTokenOption) => void;
  orderItems: Record<string, ShopifyProductToCheckout>;
  onSubmit: () => void;
  product: ProductType;
}

export const ImagePickerModule: FC<ImagePickerModuleProps> = ({
  isLoading,
  onAdd,
  onRemove,
  onSubmit,
  orderItems,
  product,
}) => {
  const [{ id }] = product.id;
  const { t } = useTranslation('nftPrintService');
  const { walletAddress } = useWeb3();
  const { gatedRelease } = useReleaseGating({ product });
  const [error, setError] = useState(true);
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<NftTokenOption[]>([]);
  const delegateCash = useDelegateCash();

  const selected = Object.values(orderItems).map((item) =>
    customPropertyByKey(item.customProperties, 'token_id'),
  );

  const fetchNfts = useCallback(async () => {
    setError(false);
    setLoading(true);
    try {
      const data = await nftsGateway.getNftPrintsForProduct(id);
      const contractAddress = gatedRelease?.nftContract as string;
      const tokens = await nftsGateway.ownedNftTokensInContract({
        chain: gatedRelease?.chainId as string,
        contractAddress,
        walletAddress: walletAddress as string,
        delegateCash: delegateCash.selectedDelegation
          ? {
              vault: delegateCash.selectedDelegation.vault,
            }
          : undefined,
      });
      const printed = (data.prints as GeneratedNftPrint[]).map(
        (p) => p.tokenId,
      );
      const sorted = nftTokenToPrintOption(
        tokens,
        printed,
        contractAddress,
      ).sort((a, b) => a.id.localeCompare(b.id, 'en', { numeric: true }));
      setOptions(sorted);
    } catch (err) {
      console.error(err);

      setError(true);
    }
    setLoading(false);
  }, [id, walletAddress, gatedRelease, delegateCash.selectedDelegation]);

  const handleAdd = useCallback(
    (tokenIds: string[]) => {
      tokenIds
        .filter((tokenId) => !selected.includes(tokenId))
        .forEach((tokenId: string) => {
          const found = options.find((o) => o.id === tokenId);
          if (found) onAdd(found);
        });
    },
    [onAdd, options, selected],
  );

  const handleRemove = useCallback(
    (tokenId: string) => {
      const found = options.find((o) => o.id === tokenId);
      if (found) onRemove(found);
    },
    [onRemove, options],
  );

  const handleRetry = useCallback(() => {
    if (gatedRelease?.nftContract && walletAddress) {
      fetchNfts();
    }
  }, [fetchNfts, gatedRelease, walletAddress]);

  useEffect(() => {
    if (gatedRelease?.nftContract && walletAddress) {
      fetchNfts();
    }
  }, [fetchNfts, gatedRelease, walletAddress, delegateCash.selectedDelegation]);

  return (
    <>
      {loading && (
        <div className="flex min-h-14 w-full items-center justify-center">
          <Spinner />
        </div>
      )}
      {error && (
        <Alert status="error">
          <span>
            {t('error1of2')}
            <button onClick={handleRetry} type="button">
              <span className="hyperlink">{t('error2of2')}</span>
            </button>
          </span>
        </Alert>
      )}
      {!loading && !error && (
        <ImagePicker
          collectionTitle={gatedRelease?.nftCollectionTitle}
          collectionUrl={gatedRelease?.nftCollectionUrl}
          defaultValues={{ option: selected }}
          loading={isLoading}
          onAdd={handleAdd}
          onRemove={handleRemove}
          onSubmit={onSubmit}
          options={options}
          printMatchesNft={!!gatedRelease?.printMatchesNft}
        />
      )}
    </>
  );
};
